// frontend/admin-portal/src/pages/RequisitionDetail.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Badge,
  Alert,
  Spinner,
  Table,
} from "@fiberhome/shared-ui";
import { requisitionService } from "../services/requisitionService";
import RequisitionStatusBadge from "../components/procurement/RequisitionStatusBadge";
import SendRfqModal from "../components/procurement/SendRfqModal";
import RequisitionModal from "../components/procurement/RequisitionModal";

export default function RequisitionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [requisition, setRequisition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await requisitionService.get(id);
      setRequisition(r);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Not found");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = async () => {
    if (
      !window.confirm("Cancel this requisition? RFQs sent will remain visible.")
    )
      return;
    try {
      await requisitionService.cancel(id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  if (loading) {
    return (
      <>
        <PageHeader
          title="Requisition"
          breadcrumbs={["Home", "Procurement", "…"]}
        />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }
  if (error || !requisition) {
    return (
      <>
        <PageHeader title="Not found" breadcrumbs={["Home", "Procurement"]} />
        <Alert variant="danger">{error || "Requisition not found"}</Alert>
        <Button
          variant="outline"
          className="mt-3"
          onClick={() => navigate("/requisitions")}
        >
          ← Back
        </Button>
      </>
    );
  }

  const itemCols = [
    {
      key: "item",
      label: "Item",
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {r.product?.name || r.freeTextName}
          </div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {r.product?.sku || r.freeTextSpec || ""}
          </div>
        </div>
      ),
    },
    { key: "quantity", label: "Qty", render: (r) => `${r.quantity} ${r.unit}` },
    {
      key: "targetPrice",
      label: "Target Price",
      render: (r) =>
        r.targetPrice ? (
          Number(r.targetPrice).toLocaleString()
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: "notes",
      label: "Notes",
      render: (r) => r.notes || <span className="text-muted">—</span>,
    },
  ];

  const rfqCols = [
    {
      key: "supplier",
      label: "Supplier",
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.supplier?.name}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {r.code}
          </div>
        </div>
      ),
    },
    {
      key: "status",
      label: "RFQ Status",
      render: (r) => <RequisitionStatusBadge status={r.status} />,
    },
    {
      key: "quotation",
      label: "Quotation",
      render: (r) =>
        r.quotation ? (
          <div>
            <Badge variant="primary">{r.quotation.code}</Badge>
            <div style={{ fontSize: 12, marginTop: 3 }}>
              {r.quotation.currency}{" "}
              {Number(r.quotation.totalAmount).toLocaleString()}
            </div>
          </div>
        ) : (
          <span className="text-muted">awaiting</span>
        ),
    },
    {
      key: "sentAt",
      label: "Sent",
      render: (r) => (r.sentAt ? new Date(r.sentAt).toLocaleDateString() : "—"),
    },
    {
      key: "actions",
      label: "",
      width: 120,
      render: (r) => (
        <Button
          size="sm"
          variant="primary"
          onClick={() => navigate(`/rfqs/${r.id}`)}
        >
          Open RFQ
        </Button>
      ),
    },
  ];

  const canEdit = requisition.status === "draft";
  const canSend = ["draft", "sent", "quoting"].includes(requisition.status);
  const canCompare = requisition.rfqs?.some((r) => r.quotation) || false;

  return (
    <>
      <PageHeader
        title={`${requisition.code} — ${requisition.title}`}
        subtitle={`${requisition.items?.length || 0} items · Priority: ${requisition.priority}`}
        breadcrumbs={["Home", "Procurement", "Requisitions", requisition.code]}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            {canCompare && (
              <Button
                variant="outline"
                onClick={() =>
                  navigate(`/requisitions/${requisition.id}/comparison`)
                }
              >
                Compare Quotes
              </Button>
            )}
            {canSend && (
              <Button variant="primary" onClick={() => setSendOpen(true)}>
                Send RFQ to Suppliers
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" onClick={() => setEditOpen(true)}>
                Edit
              </Button>
            )}
            {requisition.status !== "cancelled" &&
              requisition.status !== "completed" && (
                <Button variant="ghost" onClick={handleCancel}>
                  Cancel
                </Button>
              )}
          </div>
        }
      />

      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Card title="Status">
            <div className="fh-kv">
              <div className="fh-kv__row">
                <span className="text-muted">Status</span>
                <RequisitionStatusBadge status={requisition.status} />
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Priority</span>
                <span style={{ textTransform: "capitalize" }}>
                  {requisition.priority}
                </span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Needed By</span>
                <span>{requisition.neededBy || "—"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Sent At</span>
                <span>
                  {requisition.sentAt
                    ? new Date(requisition.sentAt).toLocaleString()
                    : "—"}
                </span>
              </div>
            </div>
          </Card>
        </div>
        <div className="col-md-8">
          <Card title="Description">
            <div style={{ whiteSpace: "pre-wrap", fontSize: 13.5 }}>
              {requisition.description || (
                <span className="text-muted">No description.</span>
              )}
            </div>
            {requisition.notes && (
              <>
                <hr />
                <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>
                  {requisition.notes}
                </div>
              </>
            )}
          </Card>
        </div>
      </div>

      <Card
        title={`Items (${requisition.items?.length || 0})`}
        className="mb-3"
      >
        <Table
          columns={itemCols}
          data={requisition.items || []}
          loading={false}
          emptyMessage="No items"
        />
      </Card>

      <Card title={`RFQs Sent (${requisition.rfqs?.length || 0})`}>
        <Table
          columns={rfqCols}
          data={requisition.rfqs || []}
          loading={false}
          emptyMessage="No RFQs sent yet — click 'Send RFQ to Suppliers'"
        />
      </Card>

      <SendRfqModal
        open={sendOpen}
        requisition={requisition}
        onClose={() => setSendOpen(false)}
        onSent={load}
      />
      <RequisitionModal
        open={editOpen}
        requisition={requisition}
        onClose={() => setEditOpen(false)}
        onSaved={load}
      />
    </>
  );
}
